import React, { useState, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { Form, Field } from "react-final-form";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { UPDATE_FILE } from "../../../queries/archiveQrys/secureQrys/fileUploadMgmtQrys";
import { ReactSelectAdapter } from "../../reuseableComponents/forms/ReactSelectAdapter";
import Label from "../../reuseableComponents/label";
import ButtonSubmit from "../../reuseableComponents/buttons/ButtonSubmit";
import ButtonProcessing from "../../reuseableComponents/buttons/ButtonProcessing";
import ErrorHandler from "../../reuseableComponents/ErrorHandler";
import { EnvironmentContext } from "../../../configuration/EnvironmentContext";
import { appConfig } from "../../../configuration/AppConfig";

async function fetchRequest(url, methodType) {
  try {
    let response = await fetch(url, {
      method: methodType
    });
    console.log("RESPONSE: ", response);
    if (response.status === 200) {
      return {
        status: 200
      };
    } else {
      let error = {
        status: 400,
        error: "Something went wrong"
      };
      return <ErrorHandler error={error} />;
    }
  } catch (err) {
    console.log("ERROR: ", err);
    return <ErrorHandler error={err} />;
  }
}

function moveFileInS3(env, bucketName, oldKey, newKey) {
  return `${appConfig.s3FileCopy}?bucketName=${bucketName}&oldKey=${oldKey}&newKey=${newKey}`;
}

function deleteFileInS3(env, bucketName, oldKey) {
  return `${appConfig.s3FileDelete}?bucketName=${bucketName}&fileName=${oldKey}`;
}

const FileUploadReviewForm = ({ fileTypes, fileMetadata, subTypes, systems, status, user }) => {
  const { env } = useContext(EnvironmentContext);
  const [submittingForm, setSubmitting] = useState(false);
  const [submitError, setError] = useState(null);
  const [submitSuccess, setSuccess] = useState(null);

  const [updateFileMetadata] = useMutation(UPDATE_FILE);
  const history = useHistory();

  async function onSubmit(data) {
    setSubmitting(true);
    let bucketName = appConfig.bucketName;
    //S3 object names cannot contain special characters like `&`
    let docType = data.docType.label.includes("&") ? data.docType.label.replace("&", "and") : data.docType.label;
    let filePath = `${fileMetadata.pwsNumber}/${docType.split(" ").join("-")}/${fileMetadata.fileName}`;

    /** Check to make sure that the correct subtype value is defined. If a user had a certain doc type selected,
      then changed that doc type to something that doesn't have a subtype, the old subtype value is still
      be present in the field and needs to be removed.
      */
    if (data.subType) {
      let check = data.subType.recordId === data.docType.value;
      if (!check) {
        data.ddwFileSubtype = null;
      } else data.ddwFileSubtype = data.subType.value;
    }

    let fileStatus = data.status.value;
    let rejectedPath = null;

    // If the file is a Lead Service Line Inventory, we need to update the file metadata without changing the status,
    // and if rejected we move its location rather than deleting it
    if (data.docType.value === 298) {
      filePath = fileMetadata.filePath;
      if (data.status.value === "Approved") {
        // Change out the /to-review/ directory for /accepted/
        const approvedFilePathArray = filePath.split("/");
        approvedFilePathArray[1] = "accepted";
        filePath = approvedFilePathArray.join("/");
      } else if (data.status.value === "Rejected") {
        rejectedPath = `lead-service-line/rejected/${data.pwsNumber}/${fileMetadata.fileName}`;
      }
    }

    // if ddw file type was changed then the original document needs to be removed for its current file location
    // and moved to a new one.
    if (fileMetadata.ddwFileType !== data.docType.value) {
      let moveFile = await fetchRequest(moveFileInS3(env, bucketName, fileMetadata.filePath, filePath), "GET");
      if (moveFile.status === 200) {
        await fetchRequest(deleteFileInS3(env, bucketName, fileMetadata.filePath), "GET");
      }
    }
    // If file is a Lead Service Line Inventory, we change its location if approved or rejected
    if (data.docType.value === 298) {
      if (data.status.value === "Rejected") {
        await fetchRequest(moveFileInS3(env, bucketName, fileMetadata.filePath, rejectedPath), "GET");
      } else if (data.status.value === "Approved") {
        await fetchRequest(moveFileInS3(env, bucketName, fileMetadata.filePath, filePath), "GET");
      }
    }
    if (data.status.value === "Rejected") {
      await fetchRequest(deleteFileInS3(env, bucketName, fileMetadata.filePath), "GET");
    }

    updateFileMetadata({
      variables: {
        ...data,
        fileId: fileMetadata.fileId,
        systemId: data.system.value,
        ddwFileType: data.docType.value,
        ddwFileSubtype: data.ddwFileSubtype,
        fileStatus,
        filePath: data.status.value !== "Rejected" ? filePath : rejectedPath,
        fileName: fileMetadata.fileName,
        lastUpdateBy: user.email,
        ddwNotes: data.ddwNotes,
        isDeleted: data.status.value === "Rejected" ? true : false,
        env,
        fileUploadDate: fileMetadata.createdOn
      },
      onCompleted: (data) => {
        setSuccess("File Update Successful");
        setSubmitting(false);
        setTimeout(() => {
          setSuccess(null);
          history.push("/ddw-admin/file-upload/file-upload-management");
        }, 4000);
      },
      onError: (error) => {
        setError(error.message);
        setSubmitting(false);
        setTimeout(() => {
          setError(null);
        }, 4000);
      }
    });
  }

  return (
    <>
      <Row>
        <Col>
          <Form
            onSubmit={onSubmit}
            keepDirtyOnReinitialize
            initialValues={{
              ...fileMetadata,
              lastUpdatedBy: user.email,
              system: { value: fileMetadata.systemId, label: fileMetadata.systemName },
              status: { value: fileMetadata.fileStatus, label: fileMetadata.fileStatus },
              docType: { value: fileMetadata.ddwFileType, label: fileMetadata.fileTypeDescription },
              subType: { value: fileMetadata.ddwFileSubtype, label: fileMetadata.ddwFileSubtype }
            }}
            render={({ handleSubmit, form, submitting, values }) => (
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <Label htmlFor='water-systems' label='Water System' />
                    <Field
                      name='system'
                      component={ReactSelectAdapter}
                      options={systems}
                      className='m-2'
                      placeholder='Search Water System / County / PWS'
                      disabled={values.fileStatus !== "Pending"}
                    />
                  </Col>
                  <Col>
                    <Label htmlFor='status-dropdown-list' label='Status' />
                    <Field
                      name='status'
                      component={ReactSelectAdapter}
                      options={[status[1], status[2], status[3]]}
                      className='m-2'
                      disabled={values.fileStatus !== "Pending"}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label htmlFor='document-type-dropdown-list' label='Document Type' />
                    <Field
                      name='docType'
                      component={ReactSelectAdapter}
                      options={fileTypes}
                      className='m-2'
                      disabled={values.fileStatus !== "Pending"}
                    />
                  </Col>
                  {values.docType.value &&
                  subTypes.filter((records) => records.pkId === values.docType.value).length > 0 ? (
                    <Col md={6}>
                      <Label htmlFor='document-subtype-dropdown-list' label='Sub-Type' />
                      <Field
                        name='subType'
                        component={ReactSelectAdapter}
                        options={subTypes.filter((records) => records.pkId === values.docType.value)}
                        className=' form-control m-2'
                        disabled={values.fileStatus !== "Pending"}
                      />
                    </Col>
                  ) : (
                    ""
                  )}
                </Row>
                {values.status.value === "Rejected" ? (
                  <Row>
                    <Col>
                      <Label htmlFor='rejection-reason' label='Notes' />
                      <Field
                        name='ddwNotes'
                        component='textarea'
                        placeholder='Let the submitter know why the upload is being rejected and what they can do to submit successfully.'
                        className='m-2 p-2 w-100'
                        style={{ borderRadius: "5px", borderColor: "grey" }}
                        disabled={values.fileStatus !== "Pending"}
                      />
                    </Col>
                  </Row>
                ) : (
                  ""
                )}
                <Row>
                  <Col>
                    {!submittingForm ? (
                      <ButtonSubmit
                        id='ddw-primary'
                        style={{ float: "right" }}
                        disabled={values.fileStatus !== "Pending"}
                      />
                    ) : (
                      <ButtonProcessing id='ddw-primary' disabled style={{ float: "right" }} />
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span className='error'>{submitError}</span>
                    <span className='success'>{submitSuccess}</span>
                  </Col>
                </Row>
              </form>
            )}
          />
        </Col>
      </Row>
    </>
  );
};

export default FileUploadReviewForm;
